'use client';

import { useQuery } from '@tanstack/react-query';
import {
  WAF_STATUS,
  toWafCheckableUrl,
  toWafStatusFromCheckOutcome,
  type WafStatusForDomain,
} from '@terra/ui-design-system';
import { wafCheckApi } from '../../clients/tester-client/waf-check-api';

export const APPLICATION_URL_WAF_STATUS_QUERY_KEY =
  'application-url-waf-status';

const WAF_STATUS_STALE_TIME_MS = 5 * 60 * 1000;

const resolveCheckableUrl = (url: string | undefined): string | undefined =>
  url?.trim() ? toWafCheckableUrl(url) : undefined;

export function useApplicationUrlWafStatus({
  url,
}: {
  url: string | undefined;
}): WafStatusForDomain {
  const checkableUrl = resolveCheckableUrl(url);
  const query = useQuery({
    queryKey: [APPLICATION_URL_WAF_STATUS_QUERY_KEY, checkableUrl],
    queryFn: async () => {
      if (!checkableUrl) {
        throw new Error('Application URL is required');
      }

      return toWafStatusFromCheckOutcome(
        await wafCheckApi.checkUrl({ url: checkableUrl }),
      );
    },
    enabled: Boolean(checkableUrl),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: WAF_STATUS_STALE_TIME_MS,
  });

  if (!checkableUrl) {
    return WAF_STATUS.idle;
  }

  if (query.isError) {
    return WAF_STATUS.error;
  }

  return query.data ?? WAF_STATUS.pending;
}
